import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/race-tracker/components/ui/dialog";
import { Button } from "@/race-tracker/components/ui/button";
import { Input } from "@/race-tracker/components/ui/input";
import { Label } from "@/race-tracker/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Switch } from "@/race-tracker/components/ui/switch";
import { Save, Plus, Trash2 } from "lucide-react";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const ANALYSTS = ["Dan", "Evan", "Ian", "James"];
const TEAM = ["Lauren", "Paul", "Kyle", "Rachel", "Kiera", "Casey", "Rande", "Max"];
const AGENCIES = ["SMG", "DCM", "DRM", "SMG/DCM", "No Client"];
const STATUSES = ["Active", "Inactive", "Done"];

export default function AdvertiserFormModal({ advertiser, open, onClose, onSave, onDelete }) {
  const isNew = !advertiser?.id;
  const [form, setForm] = useState({
    advertiser_name: "",
    candidate_name: "",
    agency: "SMG",
    account_executive: "",
    account_manager: "",
    data_analyst: "Dan",
    advertiser_team: [],
    smg_plus: false,
    status: "Active",
  });
  const [saving, setSaving] = useState(false);
  const [aeAmError, setAeAmError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) setDeleteDialogOpen(false);
  }, [open]);

  useEffect(() => {
    if (advertiser) {
      setForm({
        advertiser_name: advertiser.advertiser_name || "",
        candidate_name: advertiser.candidate_name || "",
        agency: advertiser.agency || "SMG",
        account_executive: advertiser.account_executive || "",
        account_manager: advertiser.account_manager || "",
        data_analyst: advertiser.data_analyst || "Dan",
        advertiser_team: advertiser.advertiser_team || [],
        smg_plus: advertiser.smg_plus || false,
        status: advertiser.status || "Active",
        auto_sweep_enabled: advertiser.auto_sweep_enabled || false,
        sweep_frequency: advertiser.sweep_frequency || "Daily",
      });
    } else {
      setForm({
        advertiser_name: "",
        candidate_name: "",
        agency: "SMG",
        account_executive: "",
        account_manager: "",
        data_analyst: "Dan",
        advertiser_team: [],
        smg_plus: false,
        status: "Active",
        auto_sweep_enabled: false,
        sweep_frequency: "Daily",
      });
    }
    setAeAmError("");
  }, [advertiser, open]);

  const update = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    if (key === "account_executive" || key === "account_manager") {
      const ae = key === "account_executive" ? value : form.account_executive;
      const am = key === "account_manager" ? value : form.account_manager;
      setAeAmError(ae && am && ae === am ? "AE and AM cannot be the same person" : "");
    }
  };

  const addTeamMember = () => {
    setForm((p) => ({ ...p, advertiser_team: [...(p.advertiser_team || []), { name: "", email: "" }] }));
  };

  const updateTeamMember = (index, field, value) => {
    const team = [...(form.advertiser_team || [])];
    team[index] = { ...team[index], [field]: value };
    setForm((p) => ({ ...p, advertiser_team: team }));
  };

  const removeTeamMember = (index) => {
    setForm((p) => ({ ...p, advertiser_team: (p.advertiser_team || []).filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (aeAmError) return;
    setSaving(true);
    await onSave(form, advertiser?.id);
    setSaving(false);
  };

  const advertiserLabel = advertiser?.advertiser_name || form.advertiser_name || "this advertiser";

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{isNew ? "New Advertiser" : "Edit Advertiser"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Advertiser Name *</Label>
            <Input className="h-8 text-xs" value={form.advertiser_name} onChange={(e) => update("advertiser_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Candidate Name</Label>
            <Input className="h-8 text-xs" value={form.candidate_name} onChange={(e) => update("candidate_name", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Agency *</Label>
            <Select value={form.agency || "SMG"} onValueChange={(v) => update("agency", v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AGENCIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Data Analyst *</Label>
              <Select value={form.data_analyst} onValueChange={(v) => update("data_analyst", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ANALYSTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Account Executive</Label>
              <Select value={form.account_executive || "none"} onValueChange={(v) => update("account_executive", v === "none" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {TEAM.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Account Manager</Label>
              <Select value={form.account_manager || "none"} onValueChange={(v) => update("account_manager", v === "none" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {TEAM.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {aeAmError && <p className="text-xs text-destructive">{aeAmError}</p>}

          <div className="grid grid-cols-2 gap-4 items-center">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch checked={form.smg_plus} onCheckedChange={(v) => update("smg_plus", v)} />
              <Label className="text-xs">SMG+</Label>
            </div>
          </div>

          {/* Auto Sweep */}
          <div className="space-y-2 border border-border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-semibold">Intel Auto-Sweep</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Automatically sweep all active races for this advertiser</p>
              </div>
              <Switch checked={form.auto_sweep_enabled} onCheckedChange={(v) => update("auto_sweep_enabled", v)} />
            </div>
            {form.auto_sweep_enabled && (
              <div className="space-y-1.5 pt-1">
                <Label className="text-xs">Sweep Frequency</Label>
                <Select value={form.sweep_frequency || "Daily"} onValueChange={(v) => update("sweep_frequency", v)}>
                  <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Daily">Daily</SelectItem>
                    <SelectItem value="Every 3 Days">Every 3 Days</SelectItem>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Advertiser Team */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">Advertiser Team</Label>
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={addTeamMember}>
                <Plus className="w-3 h-3" /> Add Contact
              </Button>
            </div>
            {(form.advertiser_team || []).map((member, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input className="h-7 text-xs flex-1" placeholder="Name" value={member.name || ""} onChange={(e) => updateTeamMember(i, "name", e.target.value)} />
                <Input className="h-7 text-xs flex-1" placeholder="Email" value={member.email || ""} onChange={(e) => updateTeamMember(i, "email", e.target.value)} />
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeTeamMember(i)}>
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            {!isNew && onDelete && (
              <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={() => setDeleteDialogOpen(true)}>
                <Trash2 className="w-3 h-3 mr-1" /> Delete Advertiser
              </Button>
            )}
            <div className={`flex gap-2 ${isNew || !onDelete ? "ml-auto" : ""}`}>
            <Button variant="outline" size="sm" className="text-xs" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              className="text-xs gap-1"
              onClick={handleSave}
              disabled={saving || !form.advertiser_name || !form.data_analyst || !form.agency || !!aeAmError}
            >
              <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
            </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {!isNew && onDelete && (
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete advertiser?"
        description={`Permanently remove “${advertiserLabel}”. This cannot be undone.`}
        confirmText="Delete"
        onConfirm={async () => {
          await onDelete(advertiser.id);
        }}
      />
    )}
    </>
  );
}